import {formatCanvasVersionLabel} from 'canvas-core/canvasVersionUtil';
import {CanvasRef} from 'canvas-core/chat/bnchCanvasMarkdown';
import * as CanvasFolderArtifactDAO from 'canvas-core/network/CanvasFolderArtifactDAO';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import FolderSelectorModal from 'file-browser/FolderSelectorModal';
import FileStates from 'FileStates';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {getApolloClient} from 'graphql-client/ApolloClient';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import * as RegistryUtil from 'registry/RegistryUtil';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import {getUserMessage} from 'util/Net';
import {GenericEvent} from 'util/Types';

import {ChatCanvasVersionQuery, ChatCanvasVersionQueryVariables} from './__generated/ChatCanvasVersionQuery';
import {CHAT_CANVAS_VERSION_QUERY} from './ChatCanvasVersionGraphQL';

const SAVE_CANVAS_ERROR_MESSAGE = 'Failed to save canvas.';
const OPEN_SAVED_CANVAS_ERROR_MESSAGE = 'Canvas was saved, but it could not be opened.';
const DUPLICATE_CANVAS_ARTIFACT_MESSAGE = 'An artifact with the same name already exists in this folder.';

async function canvasArtifactName(chatSessionId: string, canvas: CanvasRef): Promise<string> {
  try {
    const result = await getApolloClient().query<ChatCanvasVersionQuery, ChatCanvasVersionQueryVariables>({
      query: CHAT_CANVAS_VERSION_QUERY,
      variables: {chatSessionId, canvasVersionId: canvas.canvasVersionId},
    });
    const version = result.data.chatCanvasVersion;
    if (
      !isInaccessible(version) &&
      version.definitionId === canvas.canvasDefinitionId &&
      version.definitionName.length > 0
    ) {
      return `${version.definitionName}-${formatCanvasVersionLabel(version.versionIndex)}`;
    }
  } catch (error) {
    notifications.error(getUserMessage(error) ?? SAVE_CANVAS_ERROR_MESSAGE);
    throw error;
  }
  notifications.error(SAVE_CANVAS_ERROR_MESSAGE);
  throw new Error(SAVE_CANVAS_ERROR_MESSAGE);
}

export async function getOrCreateCanvasFolderArtifactId({
  chatSessionId,
  folderId,
  canvas,
}: {
  chatSessionId: string;
  folderId: string;
  canvas: CanvasRef;
}): Promise<string | null> {
  let existingId: string | null;
  try {
    existingId = await CanvasFolderArtifactDAO.findCanvasFolderArtifactId({
      folderId,
      canvasVersion: canvas.canvasVersionId,
    });
    // eslint-disable-next-line benchlint/always-throw-from-catch -- lookup failure keeps the canvas placeholder
  } catch {
    return null;
  }
  if (existingId != null) {
    return existingId;
  }
  const name = await canvasArtifactName(chatSessionId, canvas);
  try {
    const {createdIds} = await CanvasFolderArtifactDAO.createCanvasFolderArtifactFromChat({
      chatSessionId,
      name,
      folderId,
      canvasVersion: canvas.canvasVersionId,
    });
    const createdId = createdIds[0];
    if (createdId != null) {
      FolderDAOCache.invalidateFolderContents(folderId);
    }
    return createdId ?? null;
    // eslint-disable-next-line benchlint/always-throw-from-catch -- create failure keeps the canvas placeholder
  } catch {
    return null;
  }
}

export function saveChatCanvasToFolder({
  chatSessionId,
  name,
  canvasVersionId,
}: {
  chatSessionId: string;
  name: string;
  canvasVersionId: string;
}): void {
  FolderSelectorModal.openForSelect(
    'append',
    {
      showInventory: true,
      showNotebook: true,
      showRegistries: false,
      title: 'Save Canvas Artifact',
      submitText: 'Save',
      initialFolderId: FolderDefaults.get(FolderDefaultsKey.CANVAS_ARTIFACT) ?? null,
    },
    async (e: GenericEvent) => {
      LoadingScreen.show();
      try {
        const {folderId} = RegistryUtil.parseFolderValue(e.target.value);
        if (folderId == null) {
          notifications.error('Select a folder to save this canvas.');
          return;
        }

        let createdIds: Array<string>;
        try {
          ({createdIds} = await CanvasFolderArtifactDAO.createCanvasFolderArtifactFromChat({
            chatSessionId,
            name,
            folderId,
            canvasVersion: canvasVersionId,
          }));
          // eslint-disable-next-line benchlint/always-throw-from-catch -- expected API errors are shown as toasts
        } catch (error) {
          const userMessage = getUserMessage(error);
          if (userMessage === DUPLICATE_CANVAS_ARTIFACT_MESSAGE) {
            notifications.warn(userMessage);
          } else {
            notifications.error(userMessage ?? SAVE_CANVAS_ERROR_MESSAGE);
          }
          return;
        }

        if (createdIds.length === 0) {
          notifications.error(SAVE_CANVAS_ERROR_MESSAGE);
          return;
        }
        const createdId = createdIds[0];
        FolderDefaults.set(FolderDefaultsKey.CANVAS_ARTIFACT, folderId);
        FolderDAOCache.invalidateFolderContents(folderId);

        try {
          await FileStates.get().openFolderItem(createdId);
          // eslint-disable-next-line benchlint/always-throw-from-catch -- the artifact is already saved
        } catch (error) {
          const userMessage = getUserMessage(error);
          notifications.error(
            userMessage == null
              ? OPEN_SAVED_CANVAS_ERROR_MESSAGE
              : `${OPEN_SAVED_CANVAS_ERROR_MESSAGE} ${userMessage}`
          );
        }
      } finally {
        LoadingScreen.hide();
      }
    }
  );
}
